// Lightweight auth context. Uses fetch + credentials:'include' for httpOnly cookies.
// Falls back to Authorization: Bearer when a token is returned in the body.
import { createContext, useCallback, useContext, useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const AuthCtx = createContext(null);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.message === "string") return detail.message;
  return String(detail);
}

async function apiCall(path, opts = {}) {
  const token = localStorage.getItem("jc3_token");
  const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  // Note: NOT using credentials:'include' — platform ingress sets Allow-Origin:* which
  // is incompatible with credentialed requests. We rely on Bearer-token auth instead.
  const res = await fetch(`${API}${path}`, { ...opts, headers });
  let body = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (!res.ok) {
    const err = new Error(formatApiError(body?.detail) || `Request failed (${res.status})`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=anon, object=authed
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const data = await apiCall("/api/auth/me");
      setUser(data.user);
    } catch (e) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    const data = await apiCall("/api/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
    if (data.access_token) localStorage.setItem("jc3_token", data.access_token);
    setUser(data.user);
    try {
      const { track } = await import("../lib/analytics");
      track("login", { role: data.user?.role });
    } catch { /* ignore */ }
    return data.user;
  }, []);

  const signup = useCallback(async (payload) => {
    try {
      const { track } = await import("../lib/analytics");
      track("signup-start", { has_referral: !!payload.referral_code });
    } catch { /* ignore */ }
    const data = await apiCall("/api/auth/register", { method: "POST", body: JSON.stringify(payload) });
    if (data.access_token) localStorage.setItem("jc3_token", data.access_token);
    setUser(data.user);
    try {
      const { track } = await import("../lib/analytics");
      track("signup-complete", { package: payload.service_package });
      if (payload.referral_code) track("referral-code-used", { code: payload.referral_code });
    } catch { /* ignore */ }
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiCall("/api/auth/logout", { method: "POST" });
    } catch {
      /* ignore */
    }
    localStorage.removeItem("jc3_token");
    setUser(false);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, login, signup, logout, refetch: fetchMe, apiCall }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const v = useContext(AuthCtx);
  if (!v) throw new Error("useAuth must be inside <AuthProvider>");
  return v;
}

export { apiCall };
